import React, { useEffect, useState } from "react";
import {
    Box,
    Typography,
    IconButton,
    Card,
    CardContent,
    Button,
} from "@mui/material";
import MobileContainer from "../global/components/MobileContainer";
import { MAIN_COLOR_RED } from "../assets/styles";
import { useNavigate } from "react-router-dom";
import Navbar from "../global/components/Navbar";
import ProgressStepper from "../global/components/ProgressStepper";
import { useShoppingListRedux } from "../hooks/useShoppingListRedux";
import { SavedShoppingList } from "../redux/shoppingListSlice";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SelectShoppingListStep from "../components/SelectShoppingListStep";
import SelectPreferencesStep, { ShoppingPreferences } from "../components/SelectPreferencesStep";
import StartShoppingStep from "../components/StartShoppingStep";
import RouteSummaryDialog from "../components/RouteSummaryDialog";
import { shoppingListApi, recommendationApi, productsApi } from "../api";
import type { CalculateRouteResponse, ProductLocationResponse } from "../api/types";

const getPersonaIdFromStorage = (): string | null => {
    try {
        const raw = localStorage.getItem("selectedPersona");
        if (!raw) return null;
        const parsed = JSON.parse(raw) as
            | string
            | { id?: string; name?: string; created_at?: string };
        if (typeof parsed === "string") {
            return parsed;
        }
        return parsed?.id ?? parsed?.name ?? parsed?.created_at ?? null;
    } catch {
        return localStorage.getItem("selectedPersona");
    }
};

// Prefetch all data needed for RouteMapPage before navigation:
// - shopping list product locations
// - route-based recommended product locations
// - persona-based recommendation locations (if enabled)
async function prefetchRouteMapData(params: {
    selectedList: SavedShoppingList | null;
    preferences: ShoppingPreferences;
    routeResult: CalculateRouteResponse | null;
    personaId: string | null;
    storeId: string;
    prefetchedPersonaRecommendedProductIds?: string[];
}): Promise<{
    productLocationCache: Record<string, number>;
    personaRecommendedProductIds: string[];
}> {
    const {
        selectedList,
        preferences,
        routeResult,
        personaId,
        storeId,
        prefetchedPersonaRecommendedProductIds,
    } = params;

    const productLocationCache: Record<string, number> = {};
    let personaRecommendedProductIds: string[] = [];

    if (!selectedList || !routeResult) {
        return { productLocationCache, personaRecommendedProductIds };
    }

    // 1) GET shopping list items from backend to derive product IDs.
    // This ensures we always use the authoritative list of items from
    // /shopping-list/{id}/items before resolving locations.
    let shoppingProductIds: string[] = [];
    try {
        const detailedItems = await shoppingListApi.getShoppingListItemsDetailed(
            selectedList.id
        );
        if (detailedItems?.products?.length) {
            shoppingProductIds = detailedItems.products
                .map((p) => p.product_id)
                .filter((id): id is string => !!id);
        }
    } catch (err) {
        console.warn(
            "[RouteMap] Failed to load shopping list items for prefetch; falling back to local items map.",
            err
        );
        shoppingProductIds = Object.keys(selectedList.items || {});
    }

    // Route-based recommended products from calculate-route
    const routeRecommendedIds = routeResult.recommended_product_ids || [];

    // Persona-based recommendations (if enabled)
    if (preferences.recommendations && personaId) {
        if (prefetchedPersonaRecommendedProductIds?.length) {
            personaRecommendedProductIds = prefetchedPersonaRecommendedProductIds;
        } else {
            try {
                const personaProducts = await recommendationApi.getRecommendationsByPersona(personaId);
                personaRecommendedProductIds = personaProducts
                    .map((p) => p.product_id)
                    .filter((id): id is string => !!id);
            } catch (err) {
                console.warn("Failed to load persona recommendations during prefetch:", err);
            }
        }
    }

    const allProductIds = Array.from(
        new Set([...shoppingProductIds, ...routeRecommendedIds, ...personaRecommendedProductIds])
    );

    if (!allProductIds.length) {
        return { productLocationCache, personaRecommendedProductIds };
    }

    try {
        const results = await Promise.allSettled(
            allProductIds.map((id) => productsApi.getProductLocation(id, storeId))
        );

        results.forEach((res, index) => {
            const productId = allProductIds[index];
            if (res.status === "fulfilled") {
                const loc: ProductLocationResponse | null = res.value;
                if (loc?.node_id != null) {
                    productLocationCache[productId] = loc.node_id;
                } else {
                    productLocationCache[productId] = -1;
                }
            } else {
                productLocationCache[productId] = -1;
            }
        });
    } catch (err) {
        console.warn("Failed to prefetch product locations for route map:", err);
    }

    // Debug: log when multiple shopping-list products share the same node_id
    try {
        const nodeToShoppingProducts: Record<number, string[]> = {};
        shoppingProductIds.forEach((productId) => {
            const nodeId = productLocationCache[productId];
            if (nodeId == null || nodeId <= 0) return;
            if (!nodeToShoppingProducts[nodeId]) {
                nodeToShoppingProducts[nodeId] = [];
            }
            nodeToShoppingProducts[nodeId].push(productId);
        });

        const sharedNodes = Object.entries(nodeToShoppingProducts).filter(
            ([, ids]) => ids.length > 1
        );

        if (sharedNodes.length > 0) {
            // eslint-disable-next-line no-console
            console.log(
                "[RouteMap] Shopping list products sharing same node_id:",
                sharedNodes.map(([nodeId, ids]) => ({
                    nodeId: Number(nodeId),
                    productIds: ids,
                }))
            );
        }
    } catch {
        // best-effort debug logging only
    }

    return { productLocationCache, personaRecommendedProductIds };
}

const GeneratingRoutePage: React.FC = () => {
    const [activeStep, setActiveStep] = useState(0); // 0: List, 1: Preferences, 2: Shop
    const [selectedList, setSelectedList] = useState<SavedShoppingList | null>(null);
    const [preferences, setPreferences] = useState<ShoppingPreferences>({
        frozenItems: false,
        heavyItems: false,
        recommendations: false,
    });
    const [loadingProgress, setLoadingProgress] = useState(0);
    const [showSummaryDialog, setShowSummaryDialog] = useState(false);
    const [routeResult, setRouteResult] = useState<CalculateRouteResponse | null>(null);
    const [personaRecommendedProductIds, setPersonaRecommendedProductIds] = useState<string[]>([]);
    const [requiresRouteBackend, setRequiresRouteBackend] = useState(false);
    const [creatingCurrentList, setCreatingCurrentList] = useState(false);
    const navigate = useNavigate();
    const personaId = getPersonaIdFromStorage();

    const { savedLists, shoppingList, loadSavedLists } = useShoppingListRedux();

    // Ensure lists are loaded when component mounts
    useEffect(() => {
        loadSavedLists();
    }, [loadSavedLists]);

    // Step 3: Loading simulation
    useEffect(() => {
        if (activeStep === 2) {
            setLoadingProgress(0);
            const interval = setInterval(() => {
                setLoadingProgress((prev) => {
                    const next = prev + 2;
                    if (next >= 100) {
                        clearInterval(interval);
                        if (!requiresRouteBackend) {
                            setTimeout(() => {
                                setShowSummaryDialog(true);
                            }, 500);
                        }
                    }
                    return next;
                });
            }, 100);
            return () => clearInterval(interval);
        }
    }, [activeStep, requiresRouteBackend]);

    const handleSelectList = (list: SavedShoppingList) => {
        setSelectedList(list);
        setActiveStep(1); // Move to preferences step
    };

    const handleTogglePreference = (key: keyof ShoppingPreferences) => {
        setPreferences((prev) => ({
            ...prev,
            [key]: !prev[key],
        }));
    };

    const handleContinueToShop = async () => {
        // Move to loading step
        setActiveStep(2);
        setShowSummaryDialog(false);
        setRouteResult(null);
        setPersonaRecommendedProductIds([]);

        const needsBackend = !!selectedList && selectedList.id !== "current";
        setRequiresRouteBackend(needsBackend);

        if (!needsBackend || !selectedList) {
            console.info("[GeneratingRoute] Skipping backend route flow.", {
                hasSelectedList: Boolean(selectedList),
                selectedListId: selectedList?.id ?? null,
                needsBackend,
                recommendationsEnabled: preferences.recommendations,
                personaId,
            });
            // No backend route calculation (e.g., current/unsaved list) – just show simulated progress then summary.
            return;
        }
        // Try to use backend item_ids for the snapshot; fall back gracefully if unavailable.
        let itemIds: number[] = [];
        try {
            const detailed = await shoppingListApi.getShoppingListItemsDetailed(selectedList.id);
            if (detailed?.products?.length) {
                itemIds = detailed.products.flatMap((p) => p.item_ids || []);
            }
        } catch {
            // Ignore; best-effort only.
        }

        // Audit the current shopping list and preferences before the first backend route calculation.
        try {
            void shoppingListApi.auditEvent(selectedList.id, "route-initial-shopping-list", {
                item_ids: itemIds,
                preferences,
                persona_id: personaId,
                source: "generating-route",
            });
        } catch {
            // Best-effort only; ignore audit failures here.
        }

        if (!preferences.recommendations) {
            console.info(
                "[GeneratingRoute] Skipping persona recommendations because the preference is disabled."
            );
        } else if (!personaId) {
            console.warn(
                "[GeneratingRoute] Skipping persona recommendations because no personaId was found in localStorage.",
                {
                    selectedPersona: localStorage.getItem("selectedPersona"),
                }
            );
        } else {
            console.info(
                `[GeneratingRoute] Fetching persona recommendations for personaId="${personaId}".`
            );
        }

        // Trigger calculate-route before the progress bar completes and fetch
        // persona recommendations in parallel so the next screens already have them.
        const routePromise = shoppingListApi.calculateRoute(selectedList.id);
        const personaRecommendationsPromise =
            preferences.recommendations && personaId
                ? recommendationApi
                      .getRecommendationsByPersona(personaId)
                      .then((products) =>
                          products
                              .map((product) => product.product_id)
                              .filter((id): id is string => Boolean(id))
                      )
                      .catch((err) => {
                          console.warn(
                              "Failed to load persona recommendations during route generation:",
                              err
                          );
                          return [] as string[];
                      })
                : Promise.resolve([] as string[]);

        Promise.all([routePromise, personaRecommendationsPromise])
            .then(([result, prefetchedRecommendationIds]) => {
                if (!result || !result.route) {
                    throw new Error("Route calculation failed.");
                }
                setRouteResult(result);
                setPersonaRecommendedProductIds(prefetchedRecommendationIds);
                console.info("[GeneratingRoute] Persona recommendations fetched.", {
                    count: prefetchedRecommendationIds.length,
                    productIds: prefetchedRecommendationIds,
                });
                // Show the route summary as soon as backend responds.
                setShowSummaryDialog(true);
            })
            .catch((err) => {
                console.error("Failed to calculate route:", err);
                setRouteResult(null);
                // On backend error (e.g. solver failure, 500), stop the flow and
                // send the user back to the Preferences step with an error message.
                setRequiresRouteBackend(false);
                setActiveStep(1);
                alert(
                    "We couldn't calculate a route for this shopping list. " +
                        "Please review your list or try again."
                );
            });
    };

    const handleBackToLists = () => {
        setActiveStep(0); // Move back to list selection
    };

    const handleStartRoute = async () => {
        setShowSummaryDialog(false);

        const storeId = "1";

        const {
            productLocationCache,
            personaRecommendedProductIds: prefetchedPersonaRecommendedProductIds,
        } =
            await prefetchRouteMapData({
                selectedList,
                preferences,
                routeResult,
                personaId,
                storeId,
                prefetchedPersonaRecommendedProductIds: personaRecommendedProductIds,
            });

        navigate("/route-map", {
            state: {
                selectedList,
                preferences,
                routeResult,
                initialProductLocationCache: productLocationCache,
                personaRecommendedProductIds: prefetchedPersonaRecommendedProductIds,
            },
        });
    };

    const handleCloseSummary = () => {
        setShowSummaryDialog(false);
        setRequiresRouteBackend(false);
        setRouteResult(null);
        setActiveStep(0); // Back to List step
    };

    const handleRecommendationItemAdded = (productId: string, quantity: number) => {
        setSelectedList((prev) =>
            prev
                ? {
                      ...prev,
                      items: {
                          ...prev.items,
                          [productId]: (prev.items[productId] || 0) + quantity,
                      },
                  }
                : null
        );
    };

    const steps = ["List", "Preferences", "Shop"];

    // Use the current (unsaved) shopping list as the list for route generation.
    // This automatically creates a backend list named "Current_list <date>" and
    // uses that list for subsequent calculate-route calls.
    const handleUseCurrentShoppingList = async () => {
        if (!shoppingList || Object.keys(shoppingList).length === 0 || creatingCurrentList) return;

        try {
            setCreatingCurrentList(true);
            const now = new Date();
            const dateLabel = now.toLocaleDateString();
            const name = `Current_list ${dateLabel}`;

            const response = await shoppingListApi.createShoppingList({
                name,
                items: { ...shoppingList },
                active: false,
            });

            setSelectedList(response.list);
            // Refresh saved lists so the new list appears in other screens
            loadSavedLists();
            setActiveStep(1); // Move to Preferences step
        } catch (error) {
            console.error("Failed to create shopping list for current items:", error);
        } finally {
            setCreatingCurrentList(false);
        }
    };

    return (
        <Box sx={{ minHeight: "100vh", display: "flex", flexDirection: "column", backgroundColor: "#fff" }}>
            <Navbar />

            <MobileContainer centerContent={false}>
                {/* Header with back button and title */}
                <Box sx={{ display: "flex", alignItems: "center", mb: 2, mt: 1 }}>
                    {activeStep > 0 && (
                        <IconButton
                            onClick={() => navigate(-1)}
                            sx={{
                                color: MAIN_COLOR_RED,
                                mr: 1,
                            }}
                        >
                            <ArrowBackIcon />
                        </IconButton>
                    )}
                    <Typography variant="h6" fontWeight="bold">
                        Plan my route
                    </Typography>
                </Box>

                <ProgressStepper steps={steps} activeStep={activeStep} />

                <Box sx={{ pb: 4 }}>
                    {activeStep === 0 && (() => {
                        const totalCurrentItems = Object.values(shoppingList || {}).reduce(
                            (sum, qty) => sum + Number(qty || 0),
                            0
                        );
                        return (
                        <>
                            {/* Current Shopping List summary (same idea as /lists page) */}
                            {totalCurrentItems > 0 && (
                                <Card sx={{ mb: 2, backgroundColor: "#f5f5f5" }}>
                                    <CardContent>
                                        <Box
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "space-between",
                                            }}
                                        >
                                            <Box>
                                                <Typography variant="subtitle1" fontWeight="bold">
                                                    Current Shopping List
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    {totalCurrentItems} items
                                                </Typography>
                                            </Box>
                                            <Box sx={{ display: "flex", gap: 1 }}>
                                                <Button
                                                    variant="contained"
                                                    size="small"
                                                    onClick={handleUseCurrentShoppingList}
                                                    disabled={creatingCurrentList}
                                                    sx={{
                                                        backgroundColor: MAIN_COLOR_RED,
                                                        "&:hover": {
                                                            backgroundColor: MAIN_COLOR_RED,
                                                        },
                                                    }}
                                                >
                                                    Use this list
                                                </Button>
                                                <Button
                                                    variant="outlined"
                                                    size="small"
                                                    onClick={() => navigate("/lists")}
                                                    sx={{
                                                        borderColor: MAIN_COLOR_RED,
                                                        color: MAIN_COLOR_RED,
                                                        "&:hover": {
                                                            borderColor: MAIN_COLOR_RED,
                                                            backgroundColor: "rgba(220, 38, 38, 0.04)",
                                                        },
                                                    }}
                                                >
                                                    Manage
                                                </Button>
                                            </Box>
                                        </Box>
                                    </CardContent>
                                </Card>
                            )}

                            <SelectShoppingListStep
                                savedLists={savedLists}
                                selectedList={selectedList}
                                onSelectList={handleSelectList}
                            />
                        </>
                        );
                    })()}
                    {activeStep === 1 && (
                        <SelectPreferencesStep
                            preferences={preferences}
                            selectedList={selectedList}
                            onTogglePreference={handleTogglePreference}
                            onBack={handleBackToLists}
                            onContinue={handleContinueToShop}
                        />
                    )}
                    {activeStep === 2 && (
                        <StartShoppingStep loadingProgress={loadingProgress} />
                    )}
                </Box>
            </MobileContainer>

            {/* Route Summary Dialog */}
            <RouteSummaryDialog
                open={showSummaryDialog}
                selectedList={selectedList}
                preferences={preferences}
                personaId={personaId}
                routeResult={routeResult}
                onStart={handleStartRoute}
                onClose={handleCloseSummary}
                onRecommendationItemAdded={handleRecommendationItemAdded}
            />
        </Box>
    );
};

export default GeneratingRoutePage;
